import { PlayerState } from "../audio/Player.js";
import { Sequence } from "../audio/Sequence.js";
import { VOEmoji, VOInstrument } from "../model/DataStructure.js";
import { EmojiDelegateInfo } from "./EmojiDelegate.js";
import { TimelineItem } from "./TimelineItem.js";

export interface TimelineOptions {
    container: HTMLElement;
    instruments: VOInstrument[];
    lineCount?: number;
    audioContext: AudioContext;
    onDragStart?: (item: TimelineItem, actor: e5.Click) => void;
    onChangeState?: (p: Sequence<TimelineItem>) => void;
}

export interface TimelineAddOptions {
    emoji: VOEmoji,
    index?: number;
    lineIndex?: number;
    speed: number;
    playing?: boolean;
}

export class Timeline {
    public onDragStart = e5.Signal.create<[TimelineItem, e5.Click]>();

    public sequence: Sequence<TimelineItem>;

    private _element: HTMLElement;
    private _items: TimelineItem[] = [];
    private _list: HTMLElement;
    private _sheet: HTMLElement;
    private _placeholder: HTMLElement;
    private _scroller: HTMLElement;
    private _current: TimelineItem = null;
    private _lineCount: number;

    constructor(options: TimelineOptions) {
        options = e5.object.merge({
            lineCount: 9
        }, options);

        if (options.lineCount % 2 !== 1) {
            throw new Error("The line count for the Timeline needs to be odd not even!");
        }

        this._lineCount = options.lineCount;

        this._element = document.createElement("div");
        this._element.className = "timeline";
        this._element.innerHTML = `
        <div class="timeline__content">
            <div class="timeline__sheet">
                <div class="timeline__lines"></div>
                <div class="timeline__footer"></div>
                <div class="timeline__note-count-handle">
                    <div class="timeline__note-count-handle-icon fal fa-music-note"></div>
                    <div class="timeline__note-count-handle-label"></div>
                    <canvas class="timeline__note-count-handle-canvas"></canvas>
                </div>
                <div class="timeline__scroller base__scroll--horz">
                    <div class="timeline__emoji-list">
                        <span class="timeline__emoji-list-footer"></span>
                        <span class="timeline__emoji-list-placeholder"></span>
                    </div>
                </div>
            </div>            
        </div>
        `;

        this._placeholder = this._element.querySelector(".timeline__emoji-list-placeholder");
        this._list = this._element.querySelector(".timeline__emoji-list");
        this._sheet = this._element.querySelector(".timeline__sheet");
        this._scroller = this._element.querySelector(".timeline__scroller");

        let lineHtml = "";
        for (let i = 0; i < this._lineCount; ++i) {
            lineHtml += `<div class="timeline__lines-item" data-level="${i}"></div>`;
        }
        const lines = this._element.querySelector(".timeline__lines");
        lines.innerHTML = lineHtml;



        this.sequence = new Sequence<TimelineItem>({
            instruments: options.instruments,
            audioContext: options.audioContext,
            onTime: (s) => {
                if (!this._current) {
                    return;
                }
                this._current.progress(this.sequence.ratioCurrent);
            },
            onChangeItem: (s) => {
                this.current = s.current;
            },
            onChangeState: (s) => {
                this.updateItemsState();
            }
        });

        if (typeof options.onDragStart === "function") {
            this.onDragStart.add(options.onDragStart);
        }

        if (typeof options.onChangeState === "function") {
            this.sequence.onChangeState.add(options.onChangeState);
        }



        let can: HTMLCanvasElement = this._element.querySelector(".timeline__note-count-handle-canvas");
        let handle: HTMLElement = this._element.querySelector(".timeline__note-count-handle");
        let label: HTMLElement = this._element.querySelector(".timeline__note-count-handle-label");
        e5.Resize.create(can).add(() => {
            updateProgress();
        });
        let updateProgress = () => {

            let ratio = e5.Calc.clamp(this.sequence.noteCount / 140, 0, 1);

            handle.style.bottom = `${ratio * 100}%`;

            label.textContent = `${this.sequence.noteCount} x`;

            if (can.offsetWidth === 0 || can.offsetHeight === 0) {
                return;
            }

            can.width = can.offsetWidth;
            can.height = can.offsetHeight;

            const radius = (can.width / 2) - 3;
            const centerX = can.width / 2;
            const centerY = can.height / 2;
            const startAngle = -Math.PI * 0.5;

            let ctx = can.getContext("2d");
            ctx.clearRect(0, 0, can.width, can.height);
            ctx.beginPath();
            ctx.strokeStyle = "#ffa751";
            ctx.lineCap = "round";
            ctx.lineWidth = 1;
            ctx.arc(centerX, centerY, radius, startAngle, startAngle + Math.PI * 2 * ratio);
            ctx.stroke();
        };
        this.sequence.onChangeItems.add(() => {
            updateProgress();
        });



        options.container.appendChild(this._element);
    }

    public dragStart(info: EmojiDelegateInfo, center: e5.IPoint): boolean {
        this._element.setAttribute("data-drag", "create");

        const state = this.dragUpdate(center);
        return state.isOverSheet;
    }

    public dragMove(info: EmojiDelegateInfo, center: e5.IPoint): boolean {
        const state = this.dragUpdate(center);
        return state.isOverSheet;
    }

    public dragEnd(info: EmojiDelegateInfo, center: e5.IPoint): boolean {
        const state = this.dragUpdate(center);
        if (state.isOverSheet) {
            info.lineIndex = this.getLineIndex(center.x, center.y);
            info.index = state.placeholderIndex;
            this.add(info);
        }

        this._list.appendChild(this._placeholder);

        this._element.removeAttribute("data-drag");
        this._element.classList.toggle("timeline--drag-over", false);
        this.highlightLineIndex(-1);

        return state.isOverSheet;
    }

    private dragUpdate(center: e5.IPoint): {
        isOverSheet: boolean;
        local: e5.IPoint,
        placeholderIndex: number;
    } {
        const localList = e5.Transform.toLocal(this._list, center.x, center.y);
        const localSheet = e5.Transform.toLocal(this._sheet, center.x, center.y);

        const isOverSheet = localSheet.x >= 0
            && localSheet.x <= this._sheet.offsetWidth
            && localSheet.y >= 0
            && localSheet.y <= this._sheet.offsetHeight;
        this._element.classList.toggle("timeline--drag-over", isOverSheet);

        let placeholderIndex = -1;
        if (isOverSheet) {
            const element = this.updatePlaceholder(localList, localSheet, this._placeholder, center);

            this.highlightLineIndex(this.getLineIndex(center.x, center.y));

            if (element) {
                for (let i = 0; i < this._items.length; ++i) {
                    if (element == this._items[i].element) {
                        placeholderIndex = i;
                        break;
                    }
                }
            }
        } else {
            this._list.appendChild(this._placeholder);
            this.highlightLineIndex(-1);
        }

        return {
            isOverSheet: isOverSheet,
            placeholderIndex,
            local: localList
        };
    }

    private updatePlaceholder(localList: e5.IPoint, localSheet: e5.IPoint, placeholder: HTMLElement, center: e5.IPoint): HTMLElement | null {
        const isOverSheet = localList.x >= 0
            && localList.x <= this._list.offsetWidth
            && localList.y >= 0
            && localList.y <= this._list.offsetHeight;

        //Autoscroll
        if (isOverSheet) {
            const off = 60;
            const speed = 20;

            if (localSheet.x < off) {
                const ratio = Math.min(off - localSheet.x, off) / off;
                this._scroller.scrollLeft -= speed * ratio;
            } else if (localSheet.x > this._sheet.offsetWidth - off) {
                const ratio = Math.min(localSheet.x - (this._sheet.offsetWidth - off), off) / off;
                this._scroller.scrollLeft += speed * ratio;
            }
        }

        const scrollLeftBefore = this._scroller.scrollLeft;

        placeholder.classList.add("e5-hidden");

        const element = this.getHitItem(localList);
        if (element) {
            this._list.insertBefore(placeholder, element);
        } else {

            const items = this._element.querySelectorAll<HTMLElement>(".timeline-item:not(.timeline-item--dragging)");
            if (items.length > 0) {
                items[items.length - 1].after(placeholder);
            } else {
                this._list.appendChild(placeholder);
            }
        }

        placeholder.classList.remove("e5-hidden");

        this._scroller.scrollLeft = scrollLeftBefore;

        return element;
    }

    private getHitItem(local: e5.IPoint): HTMLElement | null {
        const items = this._element.querySelectorAll<HTMLElement>(".timeline-item:not(.timeline-item--dragging)");

        if (items.length === 0) {
            return null;
        }

        if (local.x <= 0) {
            return items[0];
        }

        const last = items[items.length - 1];
        const right = last.offsetLeft + last.offsetWidth;
        if (local.x >= right) {
            return null;
        }

        const first = items[0];
        const left = first.offsetLeft + first.offsetWidth;
        if (local.x <= left) {
            return first;
        }

        let dist = Number.MAX_VALUE;
        let finalItem: HTMLElement = null;
        let before = true;
        let itemIndex = 0;
        for (let i = 0; i < items.length; ++i) {
            const item = items[i];

            const x = item.offsetLeft + item.offsetWidth;
            const delta = x - local.x;
            const absDelta = Math.abs(delta);

            if (absDelta < dist) {
                dist = absDelta;
                itemIndex = i;
                if (delta <= 0) {
                    finalItem = item;
                    before = false;
                } else {
                    finalItem = item;
                    before = true;
                }
            }
        }

        if (before) {
            return finalItem;
        }

        if (itemIndex < items.length - 1) {
            return items[itemIndex + 1];
        }

        return null;
    }

    public get state(): PlayerState {
        return this.sequence.state;
    }

    public pause(): void {
        this.sequence.pause();
    }

    public play(): void {
        this.sequence.play();
    }

    private get current(): TimelineItem {
        return this._current;
    }

    private set current(value: TimelineItem) {
        if (this._current == value) {
            return;
        }
        this._current = value;

        //Scroll into view ... TODO: check if touch etc are currently used on the element...
        if (value) {
            if (value.element.offsetLeft > this._scroller.scrollLeft
                && value.element.offsetWidth + value.element.offsetLeft < this._scroller.scrollLeft + this._scroller.offsetWidth) {

            } else {
                value.element.scrollIntoView({
                    behavior: "smooth",
                    // inline: "center",
                    inline: "nearest",
                    block: "nearest"
                });
            }
        }
        this.updateItemsState();
    }

    private updateItemsState(): void {
        const isPlaying = this.sequence.state === "playing";
        for (let item of this._items) {
            if (!this._current) {
                item.grayscale = false;
                item.progress(0);
            } else {
                item.grayscale = item !== this._current;
                item.playing = item === this._current && isPlaying;
                if (item === this._current) {

                    item.progress(this.sequence.ratioCurrent);
                } else {
                    item.progress(0);
                }
            }
        }
    }

    public remove(items: TimelineItem[], animate: boolean = false): void {
        if (!items || items.length === 0) {
            return;
        }

        for (let item of items) {
            if (item === this.current) {
                this.current = null;
                this.sequence.stop();
            }

            this._items.splice(this._items.indexOf(item), 1);

            if (animate) {
                item.remove();
            } else {
                item.dispose();
            }
        }

        this.sequence.load(this._items);
    }

    public clear(animate: boolean = false): void {
        this.remove(this._items.slice(), animate);
    }

    public add(options: TimelineAddOptions | TimelineAddOptions[]): void {
        if (!options) {
            return;
        }

        if (!Array.isArray(options)) {
            options = [options];
        }

        if (options.length === 0) {
            return;
        }

        let playItem: TimelineItem = null;

        for (let o of options) {
            o = e5.object.merge({
                playing: false,
                lineIndex: Math.floor(this._lineCount / 2)
            }, o);

            const item = new TimelineItem({
                emoji: o.emoji,
                lineCount: this._lineCount,
                lineIndex: o.lineIndex,
                speed: o.speed,
                onRemoveRequest: (item) => {
                    this.remove([item]);
                },
                onPlayRequest: (item) => {
                    this.sequence.seekToItem(item);
                    this.sequence.play()
                },
                onSpeedChange: () => {
                    this.sequence.updateItem(item);
                },
                onPitchChange: () => {
                    this.sequence.updateItem(item);
                },
                onDragStart: (e, a) => {
                    this.onDragStart.dispatch(item, a);
                }
            });

            if (
                o.index === undefined
                || o.index == -1
                || o.index >= this._items.length
                || !this._items.length) {
                this._list.appendChild(item.element);
                this._items.push(item);
            } else {
                this._list.insertBefore(item.element, this._items[o.index].element);
                this._items.splice(o.index, 0, item);
            }

            if (o.playing) {
                playItem = item;
            }
        }

        this._list.appendChild(this._placeholder);
        this.sequence.load(this._items);
        this.updateItemsState();

        if (playItem) {
            playItem.onPlayRequest.dispatch(playItem);
        }
    }

    private getLineIndex(clientX: number, clientY: number): number {
        const lines: HTMLElement = this._element.querySelector(".timeline__lines");
        const localLines = e5.Transform.toLocal(lines, clientX, clientY);
        const yRatio = e5.Calc.clamp(localLines.y, 0, lines.offsetHeight) / lines.offsetHeight;
        const lineIndex = Math.round(yRatio * (this._lineCount - 1));
        return lineIndex;
    }

    private highlightLineIndex(lineIndex: number): void {
        this._element.querySelectorAll(".timeline__lines-item")
            .forEach((e) => {
                let level = parseInt(e.getAttribute("data-level"));
                e.classList.toggle("timeline__lines-item--active", level == lineIndex);
            });
    }
}